import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import api from "../../util/api";

const initialState = {
  createPlant: {
    loading: false,
  },
  importPlant: {
    loading: false,
  },
  getPlant: {
    loading: false,
    data: [],
    pagination: {
      page: 1,
      pageSize: 10,
      totalItems: 0,
    },
  },
  deletePlant: {
    loading: false,
  },
  editPlant: {
    loading: false,
  },
  searchAndGetPlant: {
    loading: false,
    data: [],
  },
  deleteAllPlant: {
    loading: false,
  },
  deleteSelectedPlant: {
    loading: false,
  },
};

//Plant API call
const asyncCreatePlant = createAsyncThunk(
  "admin/createPlant",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/plants/create", payload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncImportPlant = createAsyncThunk(
  "admin/importPlant",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/plants/import", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncGetPlant = createAsyncThunk(
  "admin/getPlant",
  async (payload, { rejectWithValue }) => {
    const {
      page = 1,
      limit = 10,
      sortByColumn = "",
      sortOrder = "",
      q = "",
    } = payload;
    try {
      const response = await api.get(
        `/api/plants?page=${page}&limit=${limit}&sortByColumn=${sortByColumn}&sortOrder=${sortOrder}&q=${q}`
      );
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeletePlant = createAsyncThunk(
  "admin/deletePlant",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/plants/${payload}`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncEditPlant = createAsyncThunk(
  "admin/editPlant",
  async (payload, { rejectWithValue }) => {
    const { id, ...restPayload } = payload;
    try {
      const response = await api.put(`/api/plants/${id}`, restPayload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncSearchAndGetPlant = createAsyncThunk(
  "admin/searchAndGetPlant",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/plants/search`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteAllPlant = createAsyncThunk(
  "admin/deleteAllPlant",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/plants/delete-all`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteSelectedPlant = createAsyncThunk(
  "admin/deleteSelectedPlant",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/plants/delete-many`, payload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

export const plantSlice = createSlice({
  name: "plant",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // create plant
    builder.addCase(asyncCreatePlant.pending, (state) => {
      state.createPlant = {
        loading: true,
      };
    });
    builder.addCase(asyncCreatePlant.fulfilled, (state) => {
      state.createPlant = {
        loading: false,
      };
    });
    builder.addCase(asyncCreatePlant.rejected, (state) => {
      state.createPlant = {
        loading: false,
      };
    });
    // import plant
    builder.addCase(asyncImportPlant.pending, (state) => {
      state.importPlant = {
        loading: true,
      };
    });
    builder.addCase(asyncImportPlant.fulfilled, (state) => {
      state.importPlant = {
        loading: false,
      };
    });
    builder.addCase(asyncImportPlant.rejected, (state) => {
      state.importPlant = {
        loading: false,
      };
    });
    // get plant
    builder.addCase(asyncGetPlant.pending, (state) => {
      state.getPlant = {
        loading: true,
        data: state.getPlant.data || [],
        pagination: state.getPlant.pagination,
      };
    });
    builder.addCase(asyncGetPlant.fulfilled, (state, action) => {
      state.getPlant = {
        loading: false,
        data: action.payload.data.results || [],
        pagination: action.payload.data.pagination || state.getPlant.pagination,
      };
    });
    builder.addCase(asyncGetPlant.rejected, (state) => {
      state.getPlant = {
        loading: false,
        data: [],
        pagination: {
          page: 1,
          pageSize: 10,
          totalItems: 0,
        },
      };
    });
    // delete plant
    builder.addCase(asyncDeletePlant.pending, (state) => {
      state.deletePlant = {
        loading: true,
      };
    });
    builder.addCase(asyncDeletePlant.fulfilled, (state) => {
      state.deletePlant = {
        loading: false,
      };
    });
    builder.addCase(asyncDeletePlant.rejected, (state) => {
      state.deletePlant = {
        loading: false,
      };
    });
    // edit plant
    builder.addCase(asyncEditPlant.pending, (state) => {
      state.editPlant = {
        loading: true,
      };
    });
    builder.addCase(asyncEditPlant.fulfilled, (state) => {
      state.editPlant = {
        loading: false,
      };
    });
    builder.addCase(asyncEditPlant.rejected, (state) => {
      state.editPlant = {
        loading: false,
      };
    });
    // search and get plant
    builder.addCase(asyncSearchAndGetPlant.pending, (state) => {
      state.searchAndGetPlant = {
        loading: true,
        data: state.searchAndGetPlant.data || [],
      };
    });
    builder.addCase(asyncSearchAndGetPlant.fulfilled, (state, action) => {
      state.searchAndGetPlant = {
        loading: false,
        data: action.payload.data.results || state.searchAndGetPlant.data,
      };
    });
    builder.addCase(asyncSearchAndGetPlant.rejected, (state) => {
      state.searchAndGetPlant = {
        loading: false,
        data: [],
      };
    });
    // delete all plant
    builder.addCase(asyncDeleteAllPlant.pending, (state) => {
      state.deleteAllPlant = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteAllPlant.fulfilled, (state) => {
      state.deleteAllPlant = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteAllPlant.rejected, (state) => {
      state.deleteAllPlant = {
        loading: false,
      };
    });
    // delete selected plant
    builder.addCase(asyncDeleteSelectedPlant.pending, (state) => {
      state.deleteSelectedPlant = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteSelectedPlant.fulfilled, (state) => {
      state.deleteSelectedPlant = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteSelectedPlant.rejected, (state) => {
      state.deleteSelectedPlant = {
        loading: false,
      };
    });
  },
});

export {
  asyncCreatePlant,
  asyncImportPlant,
  asyncGetPlant,
  asyncDeletePlant,
  asyncEditPlant,
  asyncSearchAndGetPlant,
  asyncDeleteAllPlant,
  asyncDeleteSelectedPlant,
};

export default plantSlice.reducer;
